import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TeamMemberCard from './components/TeamMemberCard';
import ProjectGallery from './components/ProjectGallery';
import CertificationBadges from './components/CertificationBadges';
import DayInTheLife from './components/DayInTheLife';

const TeamExcellencePage = () => {
  const teamMembers = [
    {
      id: 1,
      name: "Anbu Liyon",
      role: "Co-Founder & Full-Stack Developer",
      avatar: "/assets/anbu.png",
      avatarPosition: "center 20%",
      bio: "Passionate developer focused on planning custom systems and full-stack architecture. Anbu oversees project coordination and ensures every line of code meets our quality benchmarks.",
      topSkills: ["Project Strategy", "System Design", "Cloud Architecture"],
      skills: [
        { name: "Project Management", level: 98 },
        { name: "Full-Stack Architecture", level: 95 },
        { name: "React/Next.js", level: 92 },
        { name: "Node.js/Firebase", level: 94 },
        { name: "DevOps/CI/CD", level: 88 }
      ],
      favoriteTools: ["VS Code", "GitHub", "Jira", "Firebase"],
      linkedin: "https://in.linkedin.com/in/anbu-liyon-068352284",
      portfolio: "https://anbuliyon.bytepairs.com/",
      github: "#"
    },
    {
      id: 2,
      name: "Giridharan Sivaramakrishnan",
      role: "Co-Founder & Full-Stack Developer",
      avatar: "/assets/giri.jpg",
      bio: "Specializing in building interactive web apps, custom database schemas, and clean user interfaces. Giridharan Sivaramakrishnan focuses on writing fast, optimized code and crafting user-friendly layouts.",
      topSkills: ["Full-Stack Dev", "Database Optimization", "SaaS Engineering"],
      skills: [
        { name: "React/Redux", level: 96 },
        { name: "Next.js/SSR", level: 94 },
        { name: "TypeScript/Node", level: 92 },
        { name: "Tailwind CSS", level: 98 },
        { name: "Database Scaling", level: 90 }
      ],
      favoriteTools: ["VS Code", "Vite", "Postman", "PostgreSQL"],
      linkedin: "https://www.linkedin.com/in/giridharan-sivaramakrishnan-6325ab293/",
      portfolio: "https://giridharan.bytepairs.com/",
      github: "#"
    }
  ];

  const teamStats = [
    {
      number: "Direct",
      label: "Developer Collaboration",
      icon: "ShieldCheck",
      color: "text-primary"
    },
    {
      number: "Focused",
      label: "Two-Member Core Team",
      icon: "Zap",
      color: "text-success"
    },
    {
      number: "Tailored",
      label: "Zero bloated code",
      icon: "Rocket",
      color: "text-error"
    },
    {
      number: "Friendly",
      label: "Long-term partnership",
      icon: "Users",
      color: "text-accent"
    }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Byte Pairs Team | Expert Custom Software & Web Developers</title>
        <meta name="description" content="Meet the developers behind Byte Pairs. Our team specializes in React, React Native, Node.js, and Python, custom-building billing, CRM, and automation software." />
        <meta name="keywords" content="software developers Dharapuram, software company in Dharapuram, React developers, web developers Dharapuram, custom software team, software engineers Tamil Nadu, Giridharan Sivaramakrishnan, Giridharan, Giridharansivaramakrishnan, bytepairs giridharan, giridharan sivaramakrishnan dharapuram" />
        <meta property="og:title" content="Byte Pairs Team | Expert Custom Software & Web Developers" />
        <meta property="og:description" content="Meet the developers behind Byte Pairs. Our team specializes in React, React Native, Node.js, and Python." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/team-excellence-expertise-personality" />
        <link rel="canonical" href="https://bytepairs.com/team-excellence-expertise-personality" />

        {/* WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Byte Pairs Team | Expert Custom Software & Web Developers",
            "description": "Meet the developers behind Byte Pairs. Our team specializes in React, React Native, Node.js, and Python, custom-building billing, CRM, and automation software.",
            "url": "https://bytepairs.com/team-excellence-expertise-personality"
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://bytepairs.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Our Team",
                "item": "https://bytepairs.com/team-excellence-expertise-personality"
              }
            ]
          })}
        </script>

        {/* Organization & Founders Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Byte Pairs",
            "url": "https://bytepairs.com/",
            "logo": "https://bytepairs.com/assets/bytepairs-logo.png",
            "founder": [
              {
                "@type": "Person",
                "name": "Anbu Liyon",
                "jobTitle": "Founder & Lead Developer"
              },
              {
                "@type": "Person",
                "name": "Giridharan Sivaramakrishnan",
                "alternateName": ["Giridharan", "Giridharansivaramakrishnan"],
                "jobTitle": "Co-Founder & Technology Lead"
              }
            ]
          })}
        </script>
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
                <Icon name="Users" size={16} className="text-primary" />
                <span className="text-primary font-medium text-sm">Meet Our Team</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                The Builders Behind
                <span className="text-gradient block">Your Digital Tools</span>
              </h1>

              <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                We are a small, focused startup of skilled software developers. We work directly with you to build custom software, tools, and websites, ensuring clean code, transparent timelines, and personal developer attention throughout.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand"
                >
                  Start a Conversation
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Download"
                  iconPosition="left"
                  className="border-primary/20 text-primary hover:bg-primary/5"
                >
                  Download Team Profiles
                </Button>
              </div>
            </motion.div>

            {/* Team Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
            >
              {teamStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="card p-6 group hover:shadow-brand-lg transition-all duration-300">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon name={stat.icon} size={24} className={stat.color} />
                    </div>
                    <div className={`text-3xl font-bold ${stat.color} mb-2`}>{stat.number}</div>
                    <p className="text-text-secondary text-sm">{stat.label}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Team Members Section - Commented out for future use */}
        {/*
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Meet Our Developers
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto">
                Each team member brings unique skills and fresh perspective to every project.
                Click on any card to explore their expertise in detail.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto px-4">
              {teamMembers.map((member, index) => (
                <TeamMemberCard key={member.id} member={member} index={index} />
              ))}
            </div>
          </div>
        </section>
        */}

        {/* Project Gallery Section - COMMENTED OUT - Add back when you have projects */}
        {/* 
        <section className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Our Collective Achievements
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto">
                Explore the diverse range of projects our team has delivered, from e-commerce platforms
                to complex SaaS applications, each showcasing our technical expertise and problem-solving abilities.
              </p>
            </motion.div>

            <ProjectGallery />
          </div>
        </section>
        */}

        {/* Certifications Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <CertificationBadges />
          </div>
        </section>

        {/* Day in the Life Section */}
        <section className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <DayInTheLife />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
                <Icon name="Sparkles" size={16} className="text-primary" />
                <span className="text-primary font-medium text-sm">Ready to Work Together?</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Experience the Boutique Advantage
              </h2>

              <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
                Work directly with our dedicated team from day one and experience the difference
                that passion and personal attention makes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand"
                >
                  Schedule Team Introduction
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="border-primary/20 text-primary hover:bg-primary/5"
                >
                  View Our Technology Stack
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

        <Footer />
    </div>
  );
};

export default TeamExcellencePage;